import {
  Banner,
  Button,
  Card,
  CardContent,
  Dialog,
  Input,
  Label,
  PageHeader,
} from "@safeplate/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { useComputerActions } from "../quick-access/computer-actions.js";
import {
  removePerson,
  usePeopleOnComputer,
} from "../quick-access/people-store.js";
import { useShell } from "../shell/shell-context.js";
import { PASSWORD_MAX, PASSWORD_MIN } from "../team/add-people.js";
import { changePassword, deleteAccount } from "./account-api.js";

// Anyone signed in with a password can change it, including people whose
// manager chose their first one.
function PasswordCard() {
  const id = useId();
  const shell = useShell();
  const [formKey, setFormKey] = useState(0);
  // After a PIN unlock, signing in again with the password (as "Add another
  // person" does) is the way to change it.
  const actions = useComputerActions({
    authMethod: shell.authMethod,
    staffProfileIds: shell.ownStaffProfileIds,
    userId: shell.userId,
  });
  const save = useMutation({
    mutationFn: changePassword,
    onSuccess: () => setFormKey((value) => value + 1),
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <h2 className="text-base font-semibold text-stone-950">Password</h2>
        {shell.authMethod === "shared_pin" ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-stone-700">
              Sign in with your password to change it
            </p>
            <Button
              disabled={actions.pending}
              onClick={() => void actions.addPerson()}
              variant="outline"
            >
              Sign in
            </Button>
          </div>
        ) : (
          <form
            className="space-y-4"
            key={formKey}
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              save.mutate({
                current_password: String(form.get("current_password") ?? ""),
                new_password: String(form.get("new_password") ?? ""),
              });
            }}
          >
            <div>
              <Label htmlFor={`${id}-current`}>Current password</Label>
              <Input
                autoComplete="current-password"
                className="mt-1.5"
                id={`${id}-current`}
                name="current_password"
                required
                type="password"
              />
            </div>
            <div>
              <Label htmlFor={`${id}-new`}>New password</Label>
              <Input
                autoComplete="new-password"
                className="mt-1.5"
                id={`${id}-new`}
                maxLength={PASSWORD_MAX}
                minLength={PASSWORD_MIN}
                name="new_password"
                required
                type="password"
              />
            </div>
            {save.error ? (
              <p className="text-sm font-semibold text-red-800" role="alert">
                {displayError(save.error)}
              </p>
            ) : null}
            {save.isSuccess ? (
              <Banner title="Password changed" tone="positive" />
            ) : null}
            <div className="text-right">
              <Button disabled={save.isPending} type="submit">
                {save.isPending ? "Saving…" : "Change password"}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function DeleteAccountCard() {
  const id = useId();
  const shell = useShell();
  const people = usePeopleOnComputer();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const remove = useMutation({
    mutationFn: () => deleteAccount(password),
    onSuccess: async () => {
      if (people.deviceId) removePerson(people.deviceId, shell.userId);
      queryClient.clear();
      await navigate({ replace: true, to: "/sign-in" });
    },
  });

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
        <h2 className="text-base font-semibold text-stone-950">
          Delete account
        </h2>
        <Button onClick={() => setConfirming(true)} variant="destructive">
          Delete account
        </Button>
      </CardContent>
      <Dialog
        footer={
          <>
            <Button
              disabled={remove.isPending}
              onClick={() => setConfirming(false)}
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              disabled={remove.isPending}
              form={`${id}-form`}
              type="submit"
              variant="destructive"
            >
              {remove.isPending ? "Deleting…" : "Delete account"}
            </Button>
          </>
        }
        onClose={() => {
          if (!remove.isPending) setConfirming(false);
        }}
        open={confirming}
        size="sm"
        title="Delete your account?"
      >
        <form
          className="space-y-4"
          id={`${id}-form`}
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            remove.mutate();
          }}
        >
          <p className="text-sm text-stone-700">
            Your sign-in is removed. The records you logged stay with the
            business.
          </p>
          <div>
            <Label htmlFor={`${id}-password`}>Password</Label>
            <Input
              autoComplete="current-password"
              className="mt-1.5"
              id={`${id}-password`}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              value={password}
            />
          </div>
          {remove.error ? (
            <p className="text-sm font-semibold text-red-800" role="alert">
              {displayError(remove.error)}
            </p>
          ) : null}
        </form>
      </Dialog>
    </Card>
  );
}

export function AccountSettingsPage() {
  return (
    <div className="max-w-md space-y-6">
      <PageHeader title="Account" />
      <PasswordCard />
      <DeleteAccountCard />
    </div>
  );
}
